import React, {useCallback, useEffect} from 'react'
import styles from './UIGameTradeCreatorActionButtons.module.scss'
import IconTradeBankCheck from '@static/images/icon_trade_bank_check.svg'
import {UIGameActionButton} from '../../../common/UIGameActionButton'
import {T} from '../../../common/T'
import {TradeCreationData, TradeCreationDataValidator, TradeError} from '@colonist/game-mechanic-validators'
import {TranslationObject} from '@colonist/i18n'
import {useGameStore} from '../../../store/GameStoreContext'
import {ACTION_BUTTON_IDS} from '../../UIGameActionButtonsContainer/utils'
import {ArrayUtils} from '@colonist/utils'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator'

export const UIGameTradeCreatorActionButtonTradeWithBank = ({
    tradeCreationDataValidator,
    getCurrentTradeCreationData,
    hideTooltip,
}: {
    tradeCreationDataValidator: TradeCreationDataValidator
    getCurrentTradeCreationData: (isBankTrade: boolean) => TradeCreationData
    hideTooltip?: boolean
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const onClickCreateTrade = useGameStore((state) => state.gameTrade.onClickCreateTrade)
    const setTradeError = useGameStore((state) => state.gameTrade.setTradeError)
    const setWantedCards = useGameStore((state) => state.gameTrade.setWantedCards)
    const setSelectedCards = useGameStore((state) => state.gameCardInventory.setSelectedCards)
    const tradeCreationData = getCurrentTradeCreationData(true)
    const bankTradeError = tradeCreationDataValidator.getTradeCreationDataError(tradeCreationData)
    const isDisabled = bankTradeError != undefined && (bankTradeError.payload?.isBankTradeButtonDisabled !== false)
    const helperText: TranslationObject | undefined = bankTradeError?.error
    const currentStateValidator = useCurrentStateValidator()
    const isNotMyTurn = !currentStateValidator.currentTurnPlayers.includes(myColor)
    const beginnerSuggestionData = useGameStore((state) => state.gameBeginnerHint.beginnerHintData?.data as TradeCreationData)
    const wantedCards = useGameStore((state) => state.gameTrade.wantedCards)
    const offeredCards = useGameStore((state) => state.gameCardInventory.selectedCards)
    const setBeginnerHintType = useGameStore((state) => state.gameBeginnerHint.setBeginnerHintType)
    const setBeginnerHintData = useGameStore((state) => state.gameBeginnerHint.setBeginnerHintData)

    const checkSuggestionFulfillment = () => {
        if(beginnerSuggestionData == null) return
        if(!beginnerSuggestionData.isBankTrade) return
        const suggestedWantedCards = beginnerSuggestionData.wantedResources
        const suggestedOfferedCards = beginnerSuggestionData.offeredResources
        const isWantedCardsFulfilled = ArrayUtils.arraysEqual(wantedCards.sort(), suggestedWantedCards.sort())
        const isOfferedCardsFulfilled = ArrayUtils.arraysEqual(offeredCards.sort(), suggestedOfferedCards.sort())
        const isSuggestionFulfilled = isWantedCardsFulfilled && isOfferedCardsFulfilled
        if(isSuggestionFulfilled) {
            setBeginnerHintType('TradeBank')
        }
    }

    useEffect(() => {
        checkSuggestionFulfillment()
    }, [beginnerSuggestionData, wantedCards, offeredCards])

    const handleBankTradeClick = useCallback(() => {
        if(isDisabled) return
        onClickCreateTrade(tradeCreationData)
        setWantedCards([])
        setSelectedCards([])
        setBeginnerHintType(undefined)
        setBeginnerHintData(undefined)
    }, [isDisabled, tradeCreationData])

    const handleErrorOnClick = useCallback(() => {
        if(bankTradeError != null) {
            const tradeError: TradeError = {
                error: bankTradeError.error,
            }
            if(bankTradeError.payload?.highlightCards != null) {
                tradeError.payload = {
                    highlightCards: bankTradeError.payload.highlightCards,
                }
            }
            setTradeError(tradeError)
        }
    }, [bankTradeError])

    if(isNotMyTurn) return null
    return <UIGameActionButton
        id={ACTION_BUTTON_IDS.tradeBank}
        onClick={handleBankTradeClick}
        onClickActionIfDisabled={handleErrorOnClick}
        className={styles.actionButton}
        iconClassName={styles.icon}
        iconPath={IconTradeBankCheck}
        shakingAnimation={isDisabled}
        tooltipTitle={hideTooltip ? undefined : <T id='strings:game.tooltips.tradeWithBank'/>}
        disabled={isDisabled}
        helperText={helperText}
    />
}
